import React, { useState, useRef } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { urls } from './config.dev';
import { useNavigate } from 'react-router-dom';
import JSEncrypt from 'jsencrypt';

// ─────────────────────────────────────────────────────────────────────────────
// DESIGN TOKENS — "Vital Signal" system (matches LandingPage)
// ─────────────────────────────────────────────────────────────────────────────
const T = {
  ink:      '#0A1628',
  ink2:     '#132038',
  canvas:   '#F6F8FC',
  paper:    '#FFFFFF',
  mist:     '#EEF2FA',
  line:     'rgba(10, 22, 40, 0.08)',
  lineFirm: 'rgba(10, 22, 40, 0.14)',
  slate:    '#56637D',
  slateLt:  'rgba(10, 22, 40, 0.55)',
  brand:    '#0F2A5C',
  brandLt:  '#2452A0',
  brandPale:'#E7EDF9',
  signal:   '#0EA383',
  signalDk: '#0B7C64',
  gold:     '#D4A12A',
  danger:   '#C94A3A',
  shadow:   '0 12px 48px rgba(10, 22, 40, 0.07)',
  shadowHover: '0 20px 60px rgba(10, 22, 40, 0.13)',
};

const FONT_DISPLAY = "'Fraunces', serif";
const FONT_BODY = "'Space Grotesk', sans-serif";
const FONT_MONO = "'IBM Plex Mono', monospace";

// ─── Timing constants (JS setTimeout delays are kept in lockstep with the
// CSS animation durations/delays below, so don't change one without the other) ───
const MIN_AUTH_DISPLAY_MS   = 900;   // guarantees the key has time to slide in + wiggle at least once
const KEY_SLIDE_MS          = 450;

const SUCCESS_TURN_MS       = 400;   // key does its final turn
const SUCCESS_SHACKLE_DELAY = 380;   // shackle waits for the turn to mostly finish
const SUCCESS_SHACKLE_MS    = 550;
const NAVIGATE_DELAY_MS     = SUCCESS_SHACKLE_DELAY + SUCCESS_SHACKLE_MS + 260;

const FAIL_SHAKE_MS         = 450;
const FAIL_EJECT_DELAY      = 420;
const FAIL_EJECT_MS         = 350;
const REVERT_DELAY_MS       = FAIL_EJECT_DELAY + FAIL_EJECT_MS + 220;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// ─── Inline SVG Icon ──────────────────────────────────────────────────────
const Icon = ({ path, size = 20, color = 'currentColor', strokeWidth = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
    <path d={path} />
  </svg>
);

const ICONS = {
  clinic:      "M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6M9 9h.01M15 9h.01M9 13h.01M15 13h.01",
  lock:        "M7 11V7a5 5 0 0 1 10 0v4M5 11h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1zM12 15v2",
  eye:         "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
  eyeOff:      "M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a21.6 21.6 0 0 1 5.06-6.06M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a21.6 21.6 0 0 1-2.16 3.19M14.12 14.12a3 3 0 1 1-4.24-4.24M1 1l22 22",
  signIn:      "M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3",
  userPlus:    "M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M8.5 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM20 8v6M23 11h-6",
  spinner:     "M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83",
};

// ─── CPS Logo (identical to LandingPage) ──────────────────────────────────
const CPSLogo = ({ size = 44, dark = false }) => (
  <div style={{
    width: size, height: size, borderRadius: size * 0.28,
    background: dark ? T.ink : T.brand,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    position: 'relative', overflow: 'hidden', flexShrink: 0,
    boxShadow: dark ? '0 4px 16px rgba(0,0,0,0.1)' : '0 8px 24px rgba(15, 42, 92, 0.28)',
  }}>
    <span style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: size * 0.4, color: '#fff', letterSpacing: '-0.5px', lineHeight: 1, userSelect: 'none' }}>CPS</span>
    <div style={{ position: 'absolute', bottom: size * 0.1, right: size * 0.1, width: size * 0.14, height: size * 0.14, borderRadius: '50%', background: T.signal, opacity: 0.95 }} />
  </div>
);

// ─── Ambient particle field (identical concept to LandingPage) ──────────
const AmbientBg = () => (
  <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
    {[...Array(16)].map((_, i) => (
      <div key={i} style={{
        position: 'absolute',
        borderRadius: '50%',
        width: Math.random() * 4 + 1.5,
        height: Math.random() * 4 + 1.5,
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        background: `rgba(15, 42, 92, ${Math.random() * 0.08 + 0.04})`,
        animation: `floatParticle ${Math.random() * 22 + 14}s linear infinite`,
        animationDelay: `${Math.random() * 12}s`,
      }} />
    ))}
  </div>
);

// ─── Padlock Scene ─────────────────────────────────────────────────────────
// phase: 'authenticating' | 'success' | 'denied'
const PadlockScene = ({ phase }) => {
  const bodyColor =
    phase === 'success' ? T.signal :
    phase === 'denied'  ? T.danger :
    T.brand;

  const label =
    phase === 'success' ? 'Access granted' :
    phase === 'denied'  ? 'Incorrect credentials' :
    'Verifying credentials…';

  const keyAnimation =
    phase === 'success'
      ? `keyTurnSuccess ${SUCCESS_TURN_MS}ms cubic-bezier(0.34,1.2,0.5,1) forwards`
      : phase === 'denied'
        ? `keyShakeFail ${FAIL_SHAKE_MS}ms ease-in-out forwards, keyEjectFail ${FAIL_EJECT_MS}ms ease-in ${FAIL_EJECT_DELAY}ms forwards`
        : `keySlideIn ${KEY_SLIDE_MS}ms ease-out forwards, keyWiggle 1.1s ease-in-out ${KEY_SLIDE_MS}ms infinite`;

  const shackleAnimation =
    phase === 'success'
      ? `shackleOpen ${SUCCESS_SHACKLE_MS}ms cubic-bezier(0.34,1.35,0.64,1) ${SUCCESS_SHACKLE_DELAY}ms forwards`
      : 'none';

  const bodyAnimation =
    phase === 'denied'
      ? `bodyShakeFail ${FAIL_SHAKE_MS}ms ease-in-out ${FAIL_EJECT_DELAY - 20}ms`
      : 'none';

  return (
    <div style={{
      padding: '54px 32px 44px',
      background: T.paper,
      borderRadius: 20,
      boxShadow: T.shadow,
      border: `1px solid ${T.line}`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 320,
      animation: 'sceneFadeIn 0.32s ease-out',
    }}>
      <div style={{ position: 'relative', width: 132, height: 148, animation: bodyAnimation }}>
        <svg width={132} height={148} viewBox="0 0 132 148" style={{ overflow: 'visible' }}>
          {/* Shackle */}
          <g style={{
            transformOrigin: '95px 66px',
            animation: shackleAnimation,
            transition: 'stroke 0.25s ease',
          }}>
            <path
              d="M 41 66 L 41 44 A 25 25 0 0 1 91 44 L 91 66"
              fill="none"
              stroke={bodyColor}
              strokeWidth={14}
              strokeLinecap="round"
              style={{ transition: 'stroke 0.25s ease' }}
            />
          </g>

          {/* Body */}
          <rect
            x={26} y={66} width={80} height={66} rx={14}
            fill={bodyColor}
            style={{ transition: 'fill 0.25s ease' }}
          />
          <rect x={26} y={66} width={80} height={66} rx={14} fill="url(#bodySheen)" opacity={0.5} />

          {/* Keyhole */}
          <circle cx={66} cy={94} r={7.5} fill={T.paper} opacity={0.9} />
          <path d="M 61.5 99 L 70.5 99 L 66.5 112 A 4 4 0 0 1 61.5 112 Z" fill={T.paper} opacity={0.9} />

          <defs>
            <linearGradient id="bodySheen" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.22" />
              <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>

        {/* Key */}
        <svg
          width={64} height={40} viewBox="0 0 64 40"
          style={{
            position: 'absolute',
            left: 40, top: 84,
            transformOrigin: '10px 20px',
            opacity: phase === 'authenticating' ? 0 : 1,
            animation: keyAnimation,
          }}
        >
          <circle cx={10} cy={20} r={9} fill="none" stroke={T.gold} strokeWidth={5} />
          <rect x={17} y={17.5} width={34} height={5} fill={T.gold} rx={1.5} />
          <rect x={44} y={22.5} width={5} height={8} fill={T.gold} />
          <rect x={51} y={22.5} width={5} height={11} fill={T.gold} />
        </svg>
      </div>

      <p style={{
        marginTop: 22,
        fontSize: 13,
        fontWeight: 700,
        fontFamily: FONT_BODY,
        color: phase === 'success' ? T.signalDk : phase === 'denied' ? T.danger : T.slate,
        letterSpacing: 0.2,
        transition: 'color 0.2s ease',
      }}>
        {label}
      </p>
    </div>
  );
};

function Login() {
  const [formData, setFormData] = useState({
    clinicName: '',
    password: '',
    showPassword: false,
  });

  // 'idle' | 'authenticating' | 'success' | 'denied'
  const [phase, setPhase] = useState('idle');
  const [focusField, setFocusField] = useState(null);
  const navigate = useNavigate();
  const pendingTokenRef = useRef(null);

  const loading = phase !== 'idle';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const togglePasswordVisibility = () => {
    setFormData({ ...formData, showPassword: !formData.showPassword });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.clinicName === '' || formData.password === '') {
      toast.error('Please fill in all fields.', { position: 'top-left' });
      return;
    }

    setPhase('authenticating');

    try {
      const publicKey = `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAup3FU135mAvJT6OheYW3
pQyWf6jvS4duUMY4cXrlJXyGqu8HqvTU0ewPy6w2HhCPxWboNclkAkPhOCc4URNT
x1Grg+mCsWmfhVimP2wtfmlBCJ09cyDMYf93iGj8RFf3CshY5yhppT/pX+RgTuXw
ClpOXe24CLG2VF9suNylk+ReAMLyOxaekYofAMBvvrD4+GYPJgvkTMXCXCKp2PnO
8+OjiltNMnoyqPEZoXHTV4EXtTrjYnwzSe0WZSSuzgVMhmtdx+IS4eisSumHV1eI
wBeZwI0bYGxDCedPRassmSFgTFqkkcgIXmEP1n5w/08S/QPr2G+myKTeRqp5RJA5
PQIDAQAB
-----END PUBLIC KEY-----`;

      const encrypt = new JSEncrypt();
      encrypt.setPublicKey(publicKey);
      const encryptedPassword = encrypt.encrypt(formData.password);

      const encryptedFormData = { ...formData, password: encryptedPassword, confirmPassword: encryptedPassword };

      // Run the real request and the minimum "trying the lock" display time
      // in parallel, so a fast server response never cuts the animation short.
      const [response] = await Promise.all([
        fetch(urls.loginClinic, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(encryptedFormData),
        }),
        delay(MIN_AUTH_DISPLAY_MS),
      ]);

      const responseData = await response.json();

      if (response.ok && responseData.success) {
        pendingTokenRef.current = responseData.sessionToken;
        setPhase('success');
        toast.success('Login successful!', { position: 'top-left' });
        setTimeout(() => {
          navigate(`/dashboard?token=${pendingTokenRef.current}`);
        }, NAVIGATE_DELAY_MS);
      } else {
        setPhase('denied');
        toast.error('Login failed. Please check your credentials.', { position: 'top-left' });
        setTimeout(() => setPhase('idle'), REVERT_DELAY_MS);
      }
    } catch (error) {
      console.error('Error logging in:', error);
      setPhase('denied');
      toast.error('Login failed!', { position: 'top-left' });
      setTimeout(() => setPhase('idle'), REVERT_DELAY_MS);
    }
  };

  const inputStyle = (field) => ({
    width: '100%',
    padding: field === 'password' ? '12px 50px 12px 16px' : '12px 16px',
    fontSize: 14,
    fontFamily: FONT_BODY,
    border: `1.5px solid ${focusField === field ? T.signal : T.line}`,
    borderRadius: 10,
    outline: 'none',
    transition: 'all 0.2s ease',
    backgroundColor: focusField === field ? T.paper : T.mist,
    color: T.ink,
    boxShadow: focusField === field ? `0 0 0 4px ${T.brandPale}` : 'none',
  });

  return (
    <div style={{
      minHeight: '100vh',
      height: '100vh',
      background: T.canvas,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: FONT_BODY,
      padding: '20px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;0,9..144,800;1,9..144,500;1,9..144,600&family=Space+Grotesk:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600;700&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        body { margin: 0; background: ${T.canvas}; }

        @keyframes floatParticle {
          0% { transform: translateY(0) translateX(0) scale(1); opacity: 0.2; }
          20% { opacity: 0.6; }
          80% { opacity: 0.6; }
          100% { transform: translateY(-180px) translateX(40px) scale(0.6); opacity: 0; }
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        @keyframes sceneFadeIn {
          from { opacity: 0; transform: scale(0.96) translateY(6px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }

        @keyframes keySlideIn {
          0%   { transform: translate(46px, -10px) rotate(-28deg); opacity: 0; }
          55%  { opacity: 1; }
          100% { transform: translate(0, 0) rotate(-8deg); opacity: 1; }
        }
        @keyframes keyWiggle {
          0%, 100% { transform: translate(0,0) rotate(-8deg); }
          50%      { transform: translate(0,0) rotate(2deg); }
        }
        @keyframes keyTurnSuccess {
          0%   { transform: translate(0,0) rotate(-8deg); }
          100% { transform: translate(0,0) rotate(92deg); }
        }
        @keyframes keyShakeFail {
          0%, 100% { transform: translate(0,0) rotate(-8deg); }
          20%      { transform: translate(-4px,0) rotate(-16deg); }
          40%      { transform: translate(4px,0) rotate(0deg); }
          60%      { transform: translate(-4px,0) rotate(-16deg); }
          80%      { transform: translate(4px,0) rotate(0deg); }
        }
        @keyframes keyEjectFail {
          0%   { transform: translate(0,0) rotate(-8deg); opacity: 1; }
          100% { transform: translate(46px,-10px) rotate(-28deg); opacity: 0; }
        }

        @keyframes shackleOpen {
          0%   { transform: rotate(0deg) translate(0, 0); }
          100% { transform: rotate(-44deg) translate(-16px, -8px); }
        }
        @keyframes bodyShakeFail {
          0%, 100% { transform: translateX(0); }
          20%      { transform: translateX(-7px); }
          40%      { transform: translateX(7px); }
          60%      { transform: translateX(-7px); }
          80%      { transform: translateX(7px); }
        }

        .cps-login-btn:hover { transform: translateY(-2px); box-shadow: 0 16px 48px rgba(15, 42, 92, 0.32); }
        .cps-signup-btn:hover { border-color: ${T.signal}; background: ${T.paper}; }
        .cps-eye:hover { color: ${T.brand} !important; }
        @media (prefers-reduced-motion: reduce) {
          * { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; }
        }
      `}</style>

      <ToastContainer />
      <AmbientBg />

      {/* faint radial glow echoing the hero */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: `radial-gradient(ellipse at 50% 0%, ${T.brandPale} 0%, transparent 55%)`,
        opacity: 0.7,
      }} />

      <div style={{
        width: '100%',
        maxWidth: 420,
        position: 'relative',
        zIndex: 1,
      }}>
        {phase === 'idle' ? (
          <div style={{
            padding: '28px 32px 24px',
            background: T.paper,
            borderRadius: 20,
            boxShadow: T.shadow,
            border: `1px solid ${T.line}`,
            animation: 'sceneFadeIn 0.28s ease-out',
          }}>
            {/* Logo/Header - Minimal */}
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
                <CPSLogo size={48} dark />
              </div>

              <p style={{
                fontSize: 13,
                margin: '4px 0 0',
                color: T.slate,
                lineHeight: 1.5,
              }}>Sign in to <span style={{ color: T.brand, fontWeight: 700 }}>ClinicProSystem</span></p>
            </div>

            {/* Login Form - Compact */}
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 14 }}>
                <label style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: T.ink,
                  marginBottom: 5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontFamily: FONT_BODY,
                }}>
                  <Icon path={ICONS.clinic} size={14} color={T.brand} />
                  Clinic Name
                </label>
                <input
                  type="text"
                  name="clinicName"
                  value={formData.clinicName}
                  onChange={handleChange}
                  onFocus={() => setFocusField('clinicName')}
                  onBlur={() => setFocusField(null)}
                  required
                  placeholder="Enter your clinic name/ pharmacy /drug shop"
                  style={inputStyle('clinicName')}
                />
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: T.ink,
                  marginBottom: 5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontFamily: FONT_BODY,
                }}>
                  <Icon path={ICONS.lock} size={14} color={T.brand} />
                  Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={formData.showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    onFocus={() => setFocusField('password')}
                    onBlur={() => setFocusField(null)}
                    required
                    placeholder="Enter your password"
                    style={inputStyle('password')}
                  />
                  <span
                    className="cps-eye"
                    onClick={togglePasswordVisibility}
                    style={{
                      position: 'absolute',
                      right: 14,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      cursor: 'pointer',
                      color: T.slateLt,
                      display: 'flex',
                      transition: 'color 0.2s',
                    }}
                  >
                    <Icon path={formData.showPassword ? ICONS.eyeOff : ICONS.eye} size={17} />
                  </span>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="cps-login-btn"
                style={{
                  width: '100%',
                  padding: '13px',
                  fontSize: 14,
                  fontWeight: 700,
                  color: 'white',
                  background: `linear-gradient(135deg, ${T.brand}, ${T.brandLt})`,
                  border: 'none',
                  borderRadius: 10,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  opacity: loading ? 0.75 : 1,
                  boxShadow: '0 8px 32px rgba(15, 42, 92, 0.22)',
                  marginBottom: 10,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  fontFamily: FONT_BODY,
                }}
              >
                <Icon path={ICONS.signIn} size={16} />
                Sign In
              </button>

              <button
                type="button"
                onClick={() => navigate('/clinic-registration')}
                className="cps-signup-btn"
                style={{
                  width: '100%',
                  padding: '13px',
                  fontSize: 14,
                  fontWeight: 700,
                  color: T.ink,
                  background: T.paper,
                  border: `1.5px solid ${T.lineFirm}`,
                  borderRadius: 10,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  fontFamily: FONT_BODY,
                }}
              >
                <Icon path={ICONS.userPlus} size={16} color={T.brand} />
                Create New Account
              </button>
            </form>
          </div>
        ) : (
          <PadlockScene phase={phase} />
        )}

        {/* Footer Text - Minimal */}
        <p style={{
          textAlign: 'center',
          fontSize: 11,
          color: T.slateLt,
          marginTop: 14,
          fontFamily: FONT_MONO,
          letterSpacing: 0.2,
        }}>
          Protected by advanced encryption · ClinicProSystem v2.0
        </p>
      </div>
    </div>
  );
}

export default Login;